import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, StatusBadge, Loading, Tag } from "@/components/common";
import { BookOpen, Palette, Ruler, Microphone, ShieldWarning, ArrowRight } from "@phosphor-icons/react";

export default function CharacterBible() {
  const { data: chars, loading } = useFetch("/characters");
  const [active, setActive] = useState(0);
  if (loading || !chars) return <Loading />;
  const c = chars[active];

  return (
    <div>
      <PageHeader icon={BookOpen} title="Character Bible"
        subtitle="The permanent identity of every official character. AI generation reuses these references for maximum consistency between episodes." />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-2">
          {chars.map((ch, i) => (
            <button key={ch.id} data-testid={`bible-tab-${ch.id}`} onClick={() => setActive(i)}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${i === active ? "border-[#00E5FF]/50 bg-white/5" : "border-white/8 hover:border-white/20"}`}>
              <img src={ch.image} alt="" className="h-11 w-11 rounded-lg object-cover" />
              <div>
                <div className="font-semibold text-white text-sm">{ch.name}</div>
                <div className="text-[11px] text-zinc-500">{ch.tagline}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 space-y-6">
          <Panel className="p-0 overflow-hidden">
            <div className="relative h-52">
              <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
              <div className="absolute inset-0 scrim" />
              <div className="absolute bottom-4 left-5 flex items-center gap-3">
                <h2 className="font-display text-3xl font-black text-white">{c.name}</h2>
                {c.official && <StatusBadge status="APPROVED" />}
              </div>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-5">
              <Field label="Personality" value={c.personality} />
              <Field label="Age appearance" value={c.age_appearance} icon={Ruler} />
              <Field label="Height relationship" value={c.height} icon={Ruler} />
              <Field label="Clothing" value={c.clothing} />
              <Field label="Voice identity" value={c.voice} icon={Microphone} />
              <Field label="Speaking style" value={c.speaking_style} />
            </div>
          </Panel>

          <div className="grid sm:grid-cols-2 gap-6">
            <Panel>
              <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><Palette size={16} weight="duotone" className="text-[#00E5FF]" /> Colour palette</div>
              <div className="flex gap-2">
                {c.palette?.map((p) => (
                  <div key={p} className="flex flex-col items-center gap-1">
                    <div className="h-12 w-12 rounded-xl border border-white/10" style={{ background: p }} />
                    <span className="font-mono text-[10px] text-zinc-500">{p}</span>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel>
              <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><ShieldWarning size={16} weight="duotone" className="text-[#FFB800]" /> Consistency / negative prompt</div>
              <p className="text-sm text-zinc-300 font-mono">{c.negative_prompt}</p>
            </Panel>
          </div>

          <Panel>
            <div className="text-sm text-zinc-400 mb-3">Views & references</div>
            <div className="grid grid-cols-3 gap-3">
              {["Front view", "Side view", "Back view"].map((v) => (
                <div key={v} className="rounded-xl border border-white/8 bg-black/20 aspect-square flex flex-col items-center justify-center text-center">
                  <img src={c.image} alt={v} className="h-16 w-16 rounded-lg object-cover opacity-80" />
                  <span className="text-[11px] text-zinc-500 mt-2">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs text-zinc-500 mr-1">Saved outfits:</span>
              {c.outfits?.map((o) => <Tag key={o}>{o}</Tag>)}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs text-zinc-500 mr-1">Relationships:</span>
              {c.relationships?.map((r) => <Tag key={r}>{r}</Tag>)}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
        {Icon && <Icon size={13} weight="duotone" />} {label}
      </div>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
}
